import React, { useState, useEffect, useRef } from 'react';
import { 
  Shield, ShieldCheck, Briefcase, FileText, MessageSquare, Settings, Search, Bell, User, Lock, LogOut, MoreVertical, 
  Download, Upload, Plus, CheckCircle2, Clock, Eye, Layers, X, ChevronRight, ChevronLeft, 
  FilePlus2, ArrowUp, ArrowDown, CheckSquare, CalendarDays, ArrowRight, Edit, Trash2, Save, Calendar, 
  MapPin, Gavel, Users, Receipt, Banknote, RefreshCw, Mail, Sliders, ArrowDownAZ, Image as ImageIcon, FileEdit, 
  MoveUp, MoveDown, RotateCw, Settings2, Database, UserCog, Scan, Printer, MonitorDown, Copy, FolderSync, Menu, Paperclip, ExternalLink, Folder, FolderOpen, UploadCloud
} from 'lucide-react';
import { ROLE_NAMES, formatHebDate, handleDownloadLocalFile } from '../utils';

export default function TasksView({ tasks, cases, firmUsers, dbOps, globalSearch }) {
  const [localSearch, setLocalSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState({ title: '', caseId: '', assignee: '', deadline: '', status: 'pending' });

  const getDaysRemaining = (deadline) => {
    const today = new Date('2026-04-27T00:00:00'); 
    const target = new Date(deadline + 'T00:00:00'); 
    return Math.round((target - today) / (1000 * 60 * 60 * 24));
  };

  const getStatusBadge = (days) => {
    if (days < 0) return <span className="px-2.5 py-1 bg-red-100 text-red-700 rounded-full text-xs font-bold border border-red-200 whitespace-nowrap">באיחור ({Math.abs(days)})</span>;
    if (days === 0) return <span className="px-2.5 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-bold border border-amber-200 whitespace-nowrap">היום!</span>;
    if (days <= 3) return <span className="px-2.5 py-1 bg-amber-50 text-amber-600 rounded-full text-xs font-bold border border-amber-200 whitespace-nowrap">נותרו {days} ימ'</span>;
    return <span className="px-2.5 py-1 bg-emerald-50 text-emerald-600 rounded-full text-xs font-medium border border-emerald-200 whitespace-nowrap">נותרו {days} ימ'</span>;
  };

  const handleSaveTask = (e) => {
    e.preventDefault();
    if (editingTask) {
      dbOps.handleSave('tasks', formData, editingTask._id);
    } else {
      dbOps.handleSave('tasks', formData);
    }
    setIsModalOpen(false);
    setEditingTask(null);
  };

  const openNewModal = () => {
    setEditingTask(null);
    setFormData({ title: '', caseId: '', assignee: '', deadline: '', status: 'pending' });
    setIsModalOpen(true);
  };

  const openEditModal = (task, e) => {
    e.stopPropagation();
    setEditingTask(task);
    setFormData({ title: task.title || '', caseId: task.caseId || '', assignee: task.assignee || '', deadline: task.deadline || '', status: task.status || 'pending' });
    setIsModalOpen(true);
  };

  const combinedSearch = globalSearch || localSearch;
  const filteredTasks = tasks.filter(t => 
    (t.title || '').includes(combinedSearch) || 
    (t.assignee || '').includes(combinedSearch) || 
    (t.caseId || '').includes(combinedSearch)
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col h-full">
      <div className="p-4 md:p-6 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between bg-slate-50/50 gap-4">
        <h3 className="font-bold text-lg text-slate-800">משימות פתוחות</h3>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <Search className="w-4 h-4 absolute right-3 top-2.5 text-slate-400" />
            <input 
              type="text" 
              placeholder="סינון משימות..." 
              className="w-full pl-3 pr-9 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-500 transition-colors" 
              value={localSearch}
              onChange={e => setLocalSearch(e.target.value)}
            />
          </div>
          <button onClick={openNewModal} className="shrink-0 w-full sm:w-auto bg-blue-700 hover:bg-blue-800 text-white px-4 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors shadow-sm"><Plus className="w-4 h-4" /> משימה חדשה</button>
        </div>
      </div>
      <div className="overflow-x-auto flex-1 p-4 md:p-6">
        <div className="grid gap-4 min-w-[500px]">
          {filteredTasks.length === 0 ? <p className="text-center text-slate-400 mt-10">אין משימות מתאימות לחיפוש.</p> :
            filteredTasks.sort((a, b) => getDaysRemaining(a.deadline) - getDaysRemaining(b.deadline)).map((task) => {
            const daysRemaining = getDaysRemaining(task.deadline);
            return (
              <div key={task._id} className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-xl hover:shadow-md transition-all group cursor-pointer" onClick={(e) => openEditModal(task, e)}>
                <div className="flex items-center gap-4">
                  <button onClick={(e) => { e.stopPropagation(); dbOps.handleDelete('tasks', task._id); }} className="text-slate-300 hover:text-emerald-500 transition-colors shrink-0 p-1" title="סמן כהושלם"><CheckCircle2 className="w-6 h-6 md:w-7 md:h-7" /></button>
                  <div>
                    <h4 className="font-semibold text-slate-800 text-sm md:text-base">{task.title}</h4>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs md:text-sm text-slate-500 mt-1">
                      <span className="flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5" /> תיק: <span className="font-medium text-slate-700">{task.caseId}</span></span>
                      <span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> באחריות: <span className="font-medium text-slate-700">{task.assignee}</span></span>
                      <span className="flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> יעד: <span dir="ltr">{formatHebDate(task.deadline)}</span></span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 md:gap-4 shrink-0 md:opacity-0 md:group-hover:opacity-100 transition-opacity ml-2">
                  <div className="hidden sm:block">{getStatusBadge(daysRemaining)}</div>
                  <button onClick={(e) => openEditModal(task, e)} className="text-slate-400 hover:text-blue-600 transition-colors p-1" title="עריכה"><Edit className="w-5 h-5" /></button>
                  <button onClick={(e) => { e.stopPropagation(); dbOps.handleDelete('tasks', task._id); }} className="text-slate-400 hover:text-red-600 transition-colors p-1" title="מחיקה"><Trash2 className="w-5 h-5" /></button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 m-0" dir="rtl">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
              <h3 className="font-bold text-slate-800 flex items-center gap-2"><CheckSquare className="w-5 h-5 text-blue-600" /> {editingTask ? 'עריכת משימה' : 'משימה חדשה'}</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-1"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSaveTask} className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">כותרת המשימה <span className="text-red-500">*</span></label>
                <input required type="text" className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-blue-500" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">שיוך לתיק</label>
                <select className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-blue-500 bg-white" value={formData.caseId} onChange={e => setFormData({...formData, caseId: e.target.value})}>
                  <option value="">-- בחר תיק (אופציונלי) --</option>
                  <option value="כללי">משימה כללית למשרד</option>
                  {cases.map(c => <option key={c._id} value={c.id}>{c.id} - {c.title}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">איש צוות אחראי <span className="text-red-500">*</span></label>
                <select required className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-blue-500 bg-white" value={formData.assignee} onChange={e => setFormData({...formData, assignee: e.target.value})}>
                  <option value="" disabled>-- בחר עו"ד מטפל --</option>
                  {firmUsers.map(u => <option key={u._id} value={u.name}>{u.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">תאריך יעד <span className="text-red-500">*</span></label>
                <input required type="date" className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-blue-500" value={formData.deadline} onChange={e => setFormData({...formData, deadline: e.target.value})} />
              </div>
              
              <div className="pt-4 flex justify-end gap-3 border-t border-slate-100 mt-6">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">ביטול</button>
                <button type="submit" className="px-4 py-2.5 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-2"><Save className="w-4 h-4"/> שמור משימה</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
